import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const Login = () => {
    const [step, setStep] = useState("email"); // 'email' | 'pin'
    const [email, setEmail] = useState("");
    const [pin, setPin] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submitEmail = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/auth/request-pin", { email });
            toast.success("PIN sent — check your inbox.");
            setStep("pin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to send PIN");
        } finally {
            setLoading(false);
        }
    };

    const submitPin = async (e) => {
        e.preventDefault();
        if (pin.length !== 6) {
            toast.error("PIN must be 6 digits");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/auth/verify-pin", { email, pin });
            login(r.data.token, r.data.email);
            toast.success("Signed in");
            navigate("/due-soon", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid PIN");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{
                backgroundImage:
                    "linear-gradient(rgba(10,10,10,0.75), rgba(10,10,10,0.9)), url('https://images.pexels.com/photos/3721272/pexels-photo-3721272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
            data-testid="login-page"
        >
            <div className="dh-card w-full max-w-md m-4 p-8 relative z-10 bg-white">
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-16 w-16 flex items-center justify-center border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-ink)] p-1">
                        <img
                            src="/dragon-heat-logo.png"
                            alt="Dragon Heat Plumbing & Heating"
                            className="max-h-full max-w-full object-contain"
                        />
                    </div>
                    <div>
                        <p className="text-label">Dragon Heat</p>
                        <h1 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
                            Admin Sign-In
                        </h1>
                    </div>
                </div>

                {step === "email" && (
                    <form onSubmit={submitEmail} className="space-y-4" data-testid="login-email-form">
                        <div>
                            <label className="text-label block mb-2">Email address</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                autoFocus
                                required
                                className="dh-input"
                                placeholder="you@dragon-heat.co.uk"
                                value={email}
                                onChange={(e) => setEmail(e.target.value.trim())}
                            />
                        </div>
                        <button
                            data-testid="login-request-pin-btn"
                            type="submit"
                            disabled={loading}
                            className="dh-btn w-full"
                        >
                            <LogIn size={18} /> {loading ? "Sending…" : "Send me a PIN"}
                        </button>
                        <p className="text-xs text-neutral-500 font-mono">
                            Only whitelisted operators can sign in.
                        </p>
                    </form>
                )}

                {step === "pin" && (
                    <form onSubmit={submitPin} className="space-y-4" data-testid="login-pin-form">
                        <div>
                            <p className="text-sm mb-2">
                                We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
                            </p>
                            <label className="text-label block mb-2">Enter PIN</label>
                            <input
                                data-testid="login-pin-input"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                autoFocus
                                required
                                className="dh-input font-mono text-2xl tracking-[0.6em] text-center"
                                placeholder="••••••"
                                value={pin}
                                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            />
                        </div>
                        <button
                            data-testid="login-verify-pin-btn"
                            type="submit"
                            disabled={loading || pin.length !== 6}
                            className="dh-btn w-full"
                        >
                            {loading ? "Verifying…" : "Sign in"}
                        </button>
                        <button
                            data-testid="login-back-btn"
                            type="button"
                            className="dh-btn w-full"
                            data-variant="ghost"
                            onClick={() => {
                                setStep("email");
                                setPin("");
                            }}
                        >
                            Use a different email
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
